import { useMemo } from "react";
import { normalizedKeywords } from "@/shared/utils/Strings.ts";

/**
 * Provides a filter for entities of type E (entity must have a string id)
 * @param searchInput user input
 * @param entities list of entities to filter (at most one entity with undefined id is allowed)
 * @param labelRecipe recipe to produce a string from an entity
 */
export default function useKeywordsFilter<E extends { id: string | undefined }>(
  searchInput: string,
  entities: E[],
  labelRecipe: (e: E) => string,
): (entity: E) => boolean {
  const entitiesKeywords = useMemo(() => {
    if (entities.filter((e) => e.id === undefined).length > 1) {
      throw new Error("Only one entity with undefined id is allowed");
    }
    return new Map(
      entities.map((e) => [e.id, normalizedKeywords(labelRecipe(e))]),
    );
  }, [entities, labelRecipe]);

  const searchKeywords = useMemo(
    () => normalizedKeywords(searchInput),
    [searchInput],
  );

  return (entity: E) =>
    searchKeywords.every((keyword) =>
      (entitiesKeywords.get(entity.id) ?? []).some((word) =>
        word.includes(keyword),
      ),
    );
}
